import { all, call, put, takeEvery, takeLatest } from 'redux-saga/effects';
import { AxiosResponse } from 'axios';
import { CONSTANTS, ENDPOINTS } from 'utils';
import { logSagaErrorMessage } from 'utils/logSagaErrorMessage';
import {
	ITEMS_PER_PAGE_SIZE,
	MARKS_AVERAGE,
	PAGE_REQUEST_SIZE,
} from 'modules/admin-panel/utils';
import { requestSagaFactory, getScaleRange } from 'helpers';
import { IRequestParams, request } from 'helpers/request';
import * as types from 'modules/admin-panel/pages/results/ActionTypes';
import { IComment } from 'modules/admin-panel/models/IComment';
import {
	ICommentsAverage,
	IComparedComment,
	MarksCommentsPayload,
	RelatedCommentsPayload,
	IRangeData,
	IResponseComment,
	ComparedCommentPayload,
	SurveyRoundsData,
	IResponseRangeData,
	IResponseRound,
	IRoundParams,
} from 'modules/admin-panel/pages/results/models';
import {
	getComment,
	getCommentAverage,
	getCommentsData,
	getCompareComments,
	getMarksForEmployer,
	getRangeData,
	getRelatedComments,
	getRounds,
} from 'modules/admin-panel/pages/results/results.actions';

function* getCommentData({ payload }: { payload: number }) {
	try {
		yield put(getComment.loading());

		const getCommentParams: IRequestParams = {
			method: 'GET',
			url: `${CONSTANTS.baseApiPath}/result/comment/${payload}`,
		};

		const { data }: AxiosResponse<IComment> = yield call(
			request,
			getCommentParams,
		);

		yield put(getComment.success(data));
	} catch (e) {
		logSagaErrorMessage('get comment', e);
		yield put(getComment.fail(e));
	}
}

function* getCommentAverageData({ payload }: { payload: number }) {
	try {
		yield put(getCommentAverage.loading());

		const getCommentAverageQuery = {
			id: payload,
		};

		const getCommentAverageParams: IRequestParams = {
			params: getCommentAverageQuery,
			url: `${CONSTANTS.baseApiPath}/result/comment/average`,
		};

		const { data }: AxiosResponse<ICommentsAverage> = yield call(
			request,
			getCommentAverageParams,
		);

		yield put(getCommentAverage.success(data));
	} catch (e) {
		logSagaErrorMessage('get comment average', e);
		yield put(getCommentAverage.fail(e));
	}
}

function* getMarksForEmployerData({
	payload,
}: {
	payload: MarksCommentsPayload;
}) {
	try {
		yield put(getMarksForEmployer.loading());

		const { commentId, page, size } = payload;

		const getMarksQuery = {
			id: commentId,
			page,
			size,
		};

		const getMarksForEmployerParams: IRequestParams = {
			params: getMarksQuery,
			url: `${CONSTANTS.baseApiPath}/result/comment/${payload.commentId}/all-service`,
		};

		const { data }: AxiosResponse<IResponseComment> = yield call(
			request,
			getMarksForEmployerParams,
		);

		yield put(getMarksForEmployer.success(data));
	} catch (e) {
		logSagaErrorMessage('get marks list', e);
		yield put(getMarksForEmployer.fail(e));
	}
}

function* getRelatedCommentsData({
	payload,
}: {
	payload: RelatedCommentsPayload;
}) {
	try {
		yield put(getRelatedComments.loading());

		const getRelatedCommentsQuery = {
			id: payload.commentId,
			...payload,
		};

		const getRelatedCommentsParams: IRequestParams = {
			params: getRelatedCommentsQuery,
			url: `${CONSTANTS.baseApiPath}/result/comment/related`,
		};

		const { data }: AxiosResponse<IResponseComment> = yield call(
			request,
			getRelatedCommentsParams,
		);
		yield put(getRelatedComments.success(data));
	} catch (e) {
		logSagaErrorMessage('get related list', e);
		yield put(getRelatedComments.fail(e));
	}
}

function* getComparedCommentData({
	payload,
}: {
	payload: ComparedCommentPayload;
}) {
	try {
		yield put(getCompareComments.loading());

		const getComparedCommentQuery = {
			id: payload.commentId,
			...payload,
		};

		const getComparedCommentParams: IRequestParams = {
			params: getComparedCommentQuery,
			url: `${CONSTANTS.baseApiPath}/result/comment/compare`,
		};

		const { data }: AxiosResponse<IComparedComment[]> = yield call(
			request,
			getComparedCommentParams,
		);
		yield put(getCompareComments.success(data));
	} catch (e) {
		logSagaErrorMessage('get compared list', e);
		yield put(getCompareComments.fail(e));
	}
}

export const getRangeDataSaga = requestSagaFactory<
	{ surveyRoundId: string },
	IResponseRangeData
>({
	url: ENDPOINTS.resultDashboardRangeData,
	actionApi: getRangeData,
	logErrorMessage: 'Get comment range data',
});

export const getRoundDataSaga = requestSagaFactory<
	IRoundParams,
	IResponseRound
>({
	url: ENDPOINTS.resultDashboardRounds,
	actionApi: getRounds,
	logErrorMessage: 'Get rounds on the Comment detail page',
});

function* getAllCommentsPageDataSaga({
	payload: { commentId, roundId, surveyId },
}: {
	type: string;
	payload: { commentId: number; roundId: string; surveyId: string };
}) {
	try {
		yield put(getCommentsData.loading());

		const data: IRangeData = yield call(getRangeDataSaga, {
			payload: { surveyRoundId: roundId },
		});
		const { surveyList }: SurveyRoundsData = yield call(getRoundDataSaga, {
			payload: { surveyId },
		});
		const rounds = surveyList[0].surveyRoundList;

		yield call(getCommentData, { payload: commentId });
		yield call(getCommentAverageData, { payload: commentId });
		yield call(getRelatedCommentsData, {
			payload: {
				commentId,
				marks: MARKS_AVERAGE.all,
				page: PAGE_REQUEST_SIZE,
				size: ITEMS_PER_PAGE_SIZE,
			},
		});
		yield call(getMarksForEmployerData, {
			payload: {
				commentId,
				page: PAGE_REQUEST_SIZE,
				size: ITEMS_PER_PAGE_SIZE,
			},
		});

		yield put(
			getCommentsData.success({
				scaleRange: getScaleRange(data),
				rounds,
			}),
		);
	} catch (e) {
		logSagaErrorMessage('Get comments data saga', e);
		yield put(getCommentsData.fail(e));
	}
}

export function* commentSaga() {
	yield all([
		// @ts-ignore
		takeEvery(types.GET_COMMENT.DEFAULT, getCommentData),
		// @ts-ignore
		takeEvery(types.GET_COMMENT_AVERAGE.DEFAULT, getCommentAverageData),
		// @ts-ignore
		takeLatest(types.GET_MARKS_FOR_EMPLOYER.DEFAULT, getMarksForEmployerData),
		// @ts-ignore
		takeEvery(types.GET_ALL_RELATED_COMMENTS.DEFAULT, getRelatedCommentsData),
		// @ts-ignore
		takeEvery(types.GET_COMPARE_COMMENTS.DEFAULT, getComparedCommentData),
		// @ts-ignore
		takeEvery(types.GET_ALL_COMMENTS_DATA.DEFAULT, getAllCommentsPageDataSaga),
	]);
}
